<script setup>
import axios from 'axios';
import { computed, reactive, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import { accountTypeLabel, formatDateTime, formatMoney } from '../format';
import { locale, setLocale, SUPPORTED_LOCALES, t } from '../i18n';
import { isPhone } from '../isPhone';
import { accountView } from '../portfolioView';
import {
  busy as portfoliosBusy, groups, iisLimit, importDialogOpen, keyConfigured, loadPortfolios, openGroupDialog, portfolios,
  refresh, removeAccount, removeGroup, views,
} from '../portfolioStore';
import BottomSheet from './BottomSheet.vue';
import NotificationsSection from './NotificationsSection.vue';
import SecuritySection from './SecuritySection.vue';

const props = defineProps({
  user: {
    type: Object,
    default: null,
  },
  loggingOut: {
    type: Boolean,
    default: false,
  },
});

// «Выйти» lives at the end of «Профиль» on a phone: the account menu is desktop only (docs/design/SPEC.md, stage M1).
const emit = defineEmits(['profile-saved', 'go-home', 'logout', 'toast']);

// Phone «Профиль» (docs/design/SPEC.md, «Профиль»): groups of rows, details in bottom sheets.
const sheet = ref(null); // 'profile' | 'accounts' | 'key' | null
const openedAccount = ref(null);

const capital = computed(() => views.value[0] ?? null);
const accountRows = computed(() => portfolios.value.map(accountView));
const tbankRow = computed(() => apiRows.value.find((row) => row.source === 'TBANK') ?? apiRows.value[0] ?? null);

function openSheet(name) {
  openedAccount.value = null;
  sheet.value = name;
}

function fromAccounts(action) {
  sheet.value = null;
  action();
}

// On a phone notices are toasts.
watch(message, (text) => {
  if (text && isPhone.value) {
    emit('toast', text);
  }
});
watch(error, (text) => {
  if (text && isPhone.value) {
    emit('toast', text, 'info');
  }
});

const loading = ref(true);
const savingProfile = ref(false);
const savingLocale = ref(false);
const savingKeySource = ref('');
const error = ref('');
const message = ref('');

const profile = reactive({
  name: '',
  birth_date: '',
});

const apiRows = ref([]);
const keyDrafts = reactive({});

function sourceLabel(row) {
  const key = `settings.source.${row.source}`;
  const label = t(key);
  return label === key ? row.label : label;
}

async function loadSettings({ silent = false } = {}) {
  if (!silent) {
    loading.value = true;
  }
  error.value = '';
  try {
    const { data } = await axios.get('/user/settings');
    profile.name = data.profile.name || '';
    profile.birth_date = data.profile.birth_date || '';
    iisLimitHint.value = data.profile.iis_limit_hint ?? true;
    apiRows.value = data.api_keys;
    for (const row of data.api_keys) {
      if (keyDrafts[row.source] === undefined) {
        keyDrafts[row.source] = '';
      }
    }
  } catch (e) {
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.loadFailed'));
  } finally {
    loading.value = false;
  }
}

async function saveProfile() {
  savingProfile.value = true;
  error.value = '';
  message.value = '';
  try {
    const { data } = await axios.patch('/user/profile', {
      name: profile.name.trim() === '' ? null : profile.name.trim(),
      birth_date: profile.birth_date === '' ? null : profile.birth_date,
    });
    message.value = data.message;
    sheet.value = null;
    emit('profile-saved');
  } catch (e) {
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.profileSaveFailed'));
  } finally {
    savingProfile.value = false;
  }
}

// UI switches at once; the profile keeps the choice for other devices and emails (docs/app/SPEC.md, decision 8).
async function changeLocale(value) {
  if (value === locale.value) {
    return;
  }
  setLocale(value);
  savingLocale.value = true;
  error.value = '';
  message.value = '';
  try {
    const { data } = await axios.patch('/user/locale', { locale: value });
    message.value = data.message;
    emit('profile-saved');
  } catch (e) {
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.languageSaveFailed'));
  } finally {
    savingLocale.value = false;
  }
}

// IIS deposits block under the home buttons, on by default (docs/iis/SPEC.md, decision 7).
const iisLimitHint = ref(true);
const savingPreferences = ref(false);

async function toggleIisLimitHint() {
  const value = !iisLimitHint.value;
  iisLimitHint.value = value;
  savingPreferences.value = true;
  error.value = '';
  message.value = '';
  try {
    const { data } = await axios.patch('/user/preferences', { iis_limit_hint: value });
    message.value = data.message;
    if (value) {
      loadPortfolios({ silent: true });
    } else {
      iisLimit.value = null;
    }
  } catch (e) {
    iisLimitHint.value = !value;
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.preferencesSaveFailed'));
  } finally {
    savingPreferences.value = false;
  }
}

async function saveApiKey(source) {
  const key = (keyDrafts[source] || '').trim();
  if (!key) {
    error.value = t('settings.enterKey');
    return;
  }
  savingKeySource.value = source;
  error.value = '';
  message.value = '';
  try {
    const { data } = await axios.put('/user/api-keys', { source, api_key: key });
    keyDrafts[source] = '';
    await loadSettings({ silent: true });
    message.value = data.message;
    sheet.value = null;
    loadPortfolios({ silent: true });
    emit('profile-saved');
  } catch (e) {
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.keySaveFailed'));
  } finally {
    savingKeySource.value = '';
  }
}

async function removeApiKey(source) {
  if (!window.confirm(t('settings.confirmRemoveKey'))) {
    return;
  }
  savingKeySource.value = source;
  error.value = '';
  message.value = '';
  try {
    const { data } = await axios.delete(`/user/api-keys/${source}`);
    await loadSettings({ silent: true });
    message.value = data.message;
    sheet.value = null;
    loadPortfolios({ silent: true });
    emit('profile-saved');
  } catch (e) {
    if (e?.response?.status === 401) {
      return;
    }
    error.value = extractApiError(e, t('settings.keyRemoveFailed'));
  } finally {
    savingKeySource.value = '';
  }
}

// Keyed on id: fetchMe() after a save replaces the user object, which must not
// trigger a reload (it would wipe the success message and unsaved edits).
watch(
  () => props.user?.id,
  (id) => {
    if (id) {
      profile.name = props.user.name || '';
      profile.birth_date = props.user.birth_date || '';
      loadSettings();
    } else {
      loading.value = false;
      apiRows.value = [];
      error.value = '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <!-- Phone «Профиль» (docs/design/SPEC.md, «Профиль»): groups of rows, details in bottom sheets -->
  <div
    v-if="isPhone"
    class="w-full"
  >
    <h1 class="text-[1.75rem] font-bold leading-tight tracking-tight text-slate-100">
      {{ t('profile.title') }}
    </h1>

    <p
      v-if="loading"
      class="mt-4 text-[0.9375rem] text-slate-400"
    >
      {{ t('common.loading') }}
    </p>

    <template v-else-if="user">
      <button
        type="button"
        class="mt-4 flex min-h-16 w-full items-center gap-3 rounded-3xl bg-slate-900 px-5 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
        @click="openSheet('profile')"
      >
        <span
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-slate-800 text-[0.9375rem] font-semibold text-slate-100"
          aria-hidden="true"
        >{{ (profile.name || user.email || '?').trim().charAt(0).toUpperCase() }}</span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ profile.name || t('app.account') }}</span>
          <span class="mt-0.5 block truncate text-[0.8125rem] leading-4 text-slate-500">{{ user.email }}</span>
        </span>
        <span
          class="text-slate-500"
          aria-hidden="true"
        >›</span>
      </button>

      <h2 class="px-5 pb-1.5 pt-6 text-[0.8125rem] uppercase tracking-wide text-slate-500">
        {{ t('profile.data') }}
      </h2>
      <div class="overflow-hidden rounded-3xl bg-slate-900">
        <button
          v-if="portfolios.length > 0"
          type="button"
          class="flex min-h-14 w-full items-center gap-3 px-5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800 disabled:active:bg-transparent"
          :disabled="!keyConfigured || portfoliosBusy"
          :aria-busy="portfoliosBusy"
          @click="refresh()"
        >
          <span class="min-w-0 flex-1">
            <span
              class="block text-[0.9375rem] leading-5"
              :class="keyConfigured ? 'text-slate-100' : 'text-slate-500'"
            >{{ t('profile.refresh') }}</span>
            <span
              class="mt-0.5 block text-[0.8125rem] leading-4 text-slate-500"
              aria-live="polite"
            >{{ portfoliosBusy ? t('refresh.running') : !keyConfigured ? t('profile.needKey') : t('profile.updatedAt', { date: formatDateTime(capital?.synced_at) }) }}</span>
          </span>
          <svg
            v-if="portfoliosBusy"
            class="size-5 shrink-0 text-slate-400 motion-safe:animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-opacity="0.25"
              stroke-width="2.5"
            />
            <path
              d="M21 12a9 9 0 0 0-9-9"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
            />
          </svg>
        </button>
        <button
          v-if="portfolios.length > 0"
          type="button"
          class="flex min-h-12 w-full items-center justify-between gap-3 border-t border-slate-800 px-5 text-left text-[0.9375rem] text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          @click="openSheet('accounts')"
        >
          {{ t('profile.accounts') }}
          <span class="flex items-center gap-2 text-slate-500">
            <span class="tabular-nums">{{ portfolios.length }}</span>
            <span aria-hidden="true">›</span>
          </span>
        </button>
        <button
          v-if="keyConfigured"
          type="button"
          class="flex min-h-12 w-full items-center justify-between gap-3 border-t border-slate-800 px-5 text-left text-[0.9375rem] text-slate-100 first:border-t-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          :disabled="portfoliosBusy"
          @click="importDialogOpen = true"
        >
          {{ t('profile.import') }}
          <span
            class="text-slate-500"
            aria-hidden="true"
          >›</span>
        </button>
        <button
          v-if="tbankRow"
          type="button"
          class="flex min-h-12 w-full items-center justify-between gap-3 border-t border-slate-800 px-5 text-left text-[0.9375rem] text-slate-100 first:border-t-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          @click="openSheet('key')"
        >
          {{ t('profile.apiKey') }}
          <span class="flex items-center gap-2">
            <span :class="tbankRow.configured ? 'text-slate-500' : 'text-amber-400'">{{ tbankRow.configured ? t('profile.keySet') : t('profile.keyMissing') }}</span>
            <span
              class="text-slate-500"
              aria-hidden="true"
            >›</span>
          </span>
        </button>
      </div>

      <h2 class="px-5 pb-1.5 pt-6 text-[0.8125rem] uppercase tracking-wide text-slate-500">
        {{ t('profile.app') }}
      </h2>
      <div class="overflow-hidden rounded-3xl bg-slate-900">
        <div class="flex min-h-12 items-center justify-between gap-3 px-5 py-2">
          <span
            id="language-row-label"
            class="text-[0.9375rem] text-slate-100"
          >{{ t('settings.language') }}</span>
          <div
            class="flex rounded-full bg-slate-800 p-0.5"
            role="radiogroup"
            aria-labelledby="language-row-label"
          >
            <button
              v-for="code in SUPPORTED_LOCALES"
              :key="code"
              type="button"
              role="radio"
              :aria-checked="locale === code"
              class="h-8 rounded-full px-3 text-[0.875rem] font-medium disabled:opacity-50"
              :class="locale === code ? 'bg-slate-100 text-slate-950' : 'text-slate-300'"
              :disabled="savingLocale"
              @click="changeLocale(code)"
            >
              {{ t(`settings.locale.${code}`) }}
            </button>
          </div>
        </div>
        <div class="flex min-h-12 items-center justify-between gap-3 px-5 py-2">
          <span
            id="iis-limit-row-label"
            class="text-[0.9375rem] text-slate-100"
          >{{ t('settings.iisLimit') }}</span>
          <button
            type="button"
            role="switch"
            :aria-checked="iisLimitHint"
            aria-labelledby="iis-limit-row-label"
            class="relative inline-flex h-[31px] w-[51px] shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
            :class="iisLimitHint ? 'bg-emerald-600' : 'bg-slate-700'"
            :disabled="savingPreferences"
            @click="toggleIisLimitHint"
          >
            <span
              class="inline-block size-[27px] rounded-full bg-white shadow transition-transform"
              :class="iisLimitHint ? 'translate-x-[22px]' : 'translate-x-0.5'"
            />
          </button>
        </div>
        <SecuritySection
          rows
          @message="(text) => { error = ''; message = text; }"
          @error="(text) => { message = ''; error = text; }"
        />
      </div>

      <NotificationsSection
        rows
        @message="(text) => { error = ''; message = text; }"
        @error="(text) => { message = ''; error = text; }"
      />

      <button
        type="button"
        class="mt-6 flex min-h-12 w-full items-center rounded-3xl bg-slate-900 px-5 text-left text-[0.9375rem] text-rose-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800 disabled:opacity-50"
        :disabled="loggingOut"
        @click="emit('logout')"
      >
        {{ t('app.logout') }}
      </button>
    </template>

    <BottomSheet
      :open="sheet === 'profile'"
      :title="t('settings.profile')"
      @close="sheet = null"
    >
      <form
        class="space-y-3 px-5 pb-2"
        @submit.prevent="saveProfile"
      >
        <label class="block text-[0.8125rem] text-slate-400">
          {{ t('settings.namePlaceholder') }}
          <input
            v-model.trim="profile.name"
            type="text"
            class="mt-1 h-11 w-full rounded-2xl bg-slate-800 px-4 text-[0.9375rem] text-slate-100"
          >
        </label>
        <label class="block text-[0.8125rem] text-slate-400">
          {{ t('profile.birthDate') }}
          <input
            v-model="profile.birth_date"
            type="date"
            class="mt-1 h-11 w-full rounded-2xl bg-slate-800 px-4 text-[0.9375rem] text-slate-100"
          >
        </label>
        <button
          type="submit"
          class="h-11 w-full rounded-2xl bg-emerald-600 text-[0.9375rem] font-medium text-white disabled:opacity-50"
          :disabled="savingProfile"
        >
          {{ savingProfile ? t('common.saving') : t('settings.saveProfile') }}
        </button>
      </form>
    </BottomSheet>

    <BottomSheet
      :open="sheet === 'accounts'"
      :title="t('profile.accounts')"
      @close="sheet = null"
    >
      <p class="px-5 pb-1.5 pt-1 text-[0.8125rem] uppercase tracking-wide text-slate-500">
        {{ t('portfolios.accountsSection') }}
      </p>
      <ul>
        <li
          v-for="account in accountRows"
          :key="account.key"
          class="border-t border-slate-800 first:border-t-0"
        >
          <button
            type="button"
            class="flex min-h-14 w-full items-center gap-3 px-5 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
            :aria-expanded="openedAccount === account.key"
            @click="openedAccount = openedAccount === account.key ? null : account.key"
          >
            <span class="min-w-0 flex-1">
              <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ account.name || account.account_id }}</span>
              <span class="mt-0.5 block text-[0.8125rem] leading-4 text-slate-500">{{ accountTypeLabel(account.type) }}</span>
            </span>
            <span class="shrink-0 text-[0.9375rem] tabular-nums text-slate-400">{{ formatMoney(account.total_amount, account.currency) }}</span>
          </button>
          <div
            v-if="openedAccount === account.key"
            class="grid grid-cols-2 gap-2 px-5 pb-3"
          >
            <button
              type="button"
              class="h-11 rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700 disabled:opacity-50"
              :disabled="!keyConfigured || portfoliosBusy"
              @click="fromAccounts(() => refresh([account.account_id]))"
            >
              {{ t('portfolio.refresh') }}
            </button>
            <button
              type="button"
              class="h-11 rounded-2xl bg-rose-900/60 text-[0.9375rem] font-medium text-rose-200 active:bg-rose-800/60 disabled:opacity-50"
              :disabled="portfoliosBusy"
              @click="fromAccounts(() => removeAccount(account))"
            >
              {{ t('portfolio.remove') }}
            </button>
          </div>
        </li>
      </ul>

      <template v-if="groups.length > 0">
        <p class="border-t border-slate-800 px-5 pb-1.5 pt-4 text-[0.8125rem] uppercase tracking-wide text-slate-500">
          {{ t('portfolios.groupsSection') }}
        </p>
        <ul>
          <li
            v-for="group in groups"
            :key="group.id"
            class="border-t border-slate-800 first:border-t-0"
          >
            <button
              type="button"
              class="flex min-h-12 w-full items-center px-5 text-left text-[0.9375rem] text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
              :aria-expanded="openedAccount === `g:${group.id}`"
              @click="openedAccount = openedAccount === `g:${group.id}` ? null : `g:${group.id}`"
            >
              <span class="min-w-0 flex-1 truncate">{{ group.name }}</span>
            </button>
            <div
              v-if="openedAccount === `g:${group.id}`"
              class="grid grid-cols-2 gap-2 px-5 pb-3"
            >
              <button
                type="button"
                class="h-11 rounded-2xl bg-slate-800 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700"
                @click="fromAccounts(() => openGroupDialog(group))"
              >
                {{ t('portfolio.edit') }}
              </button>
              <button
                type="button"
                class="h-11 rounded-2xl bg-rose-900/60 text-[0.9375rem] font-medium text-rose-200 active:bg-rose-800/60 disabled:opacity-50"
                :disabled="portfoliosBusy"
                @click="fromAccounts(() => removeGroup(group))"
              >
                {{ t('portfolio.removeGroup') }}
              </button>
            </div>
          </li>
        </ul>
      </template>

      <div class="mt-2 border-t border-slate-800">
        <button
          type="button"
          class="flex min-h-12 w-full items-center px-5 text-left text-[0.9375rem] text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
          @click="fromAccounts(() => openGroupDialog())"
        >
          {{ t('portfolios.newGroup') }}
        </button>
      </div>
    </BottomSheet>

    <BottomSheet
      :open="sheet === 'key'"
      :title="t('profile.apiKey')"
      @close="sheet = null"
    >
      <form
        v-if="tbankRow"
        class="space-y-3 px-5 pb-2"
        @submit.prevent="saveApiKey(tbankRow.source)"
      >
        <p class="text-[0.8125rem] text-slate-400">
          {{ t('settings.apiKeysHint') }}
        </p>
        <input
          v-model="keyDrafts[tbankRow.source]"
          type="password"
          autocomplete="off"
          :aria-label="t('profile.apiKey')"
          :placeholder="tbankRow.configured ? t('settings.keyReplacePlaceholder') : t('settings.keyPlaceholder')"
          class="h-11 w-full rounded-2xl bg-slate-800 px-4 text-[0.9375rem] text-slate-100"
        >
        <button
          type="submit"
          class="h-11 w-full rounded-2xl bg-emerald-600 text-[0.9375rem] font-medium text-white disabled:opacity-50"
          :disabled="savingKeySource === tbankRow.source"
        >
          {{ savingKeySource === tbankRow.source ? t('common.saving') : t('settings.saveKey') }}
        </button>
        <button
          v-if="tbankRow.configured"
          type="button"
          class="h-11 w-full rounded-2xl bg-rose-900/60 text-[0.9375rem] font-medium text-rose-200 disabled:opacity-50"
          :disabled="savingKeySource === tbankRow.source"
          @click="removeApiKey(tbankRow.source)"
        >
          {{ t('common.remove') }}
        </button>
      </form>
    </BottomSheet>
  </div>

  <div
    v-else
    class="w-full space-y-6"
  >
    <div class="flex items-center justify-between gap-4 border-b border-slate-800 pb-4">
      <h2 class="text-lg font-semibold tracking-tight text-slate-100">
        {{ t('settings.title') }}
      </h2>
      <button
        type="button"
        class="text-sm text-slate-400 transition hover:text-slate-200"
        @click="emit('go-home')"
      >
        ← {{ t('settings.home') }}
      </button>
    </div>

    <p
      v-if="loading"
      class="text-sm text-slate-400"
    >
      {{ t('common.loading') }}
    </p>

    <template v-else-if="user">
      <div class="grid gap-6 lg:grid-cols-2 lg:gap-8">
        <section class="rounded-lg border border-slate-700 bg-slate-900/40 p-5">
          <h3 class="text-sm font-medium text-slate-300">
            {{ t('settings.profile') }}
          </h3>
          <p class="mt-1 text-xs text-slate-500">
            {{ user.email }}
          </p>
          <div class="mt-4 space-y-3">
            <input
              v-model.trim="profile.name"
              type="text"
              :placeholder="t('settings.namePlaceholder')"
              class="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            >
            <input
              v-model="profile.birth_date"
              type="date"
              class="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
            >
            <button
              type="button"
              class="rounded bg-emerald-600 px-4 py-2 text-sm font-medium hover:bg-emerald-500 disabled:opacity-50"
              :disabled="savingProfile"
              @click="saveProfile"
            >
              {{ savingProfile ? t('common.saving') : t('settings.saveProfile') }}
            </button>
          </div>
        </section>

        <section class="rounded-lg border border-slate-700 bg-slate-900/40 p-5">
          <h3 class="text-sm font-medium text-slate-300">
            {{ t('settings.language') }}
          </h3>
          <p class="mt-1 text-xs text-slate-500">
            {{ t('settings.languageHint') }}
          </p>
          <div
            class="mt-4 flex gap-2"
            role="radiogroup"
            :aria-label="t('settings.language')"
          >
            <button
              v-for="code in SUPPORTED_LOCALES"
              :key="code"
              type="button"
              role="radio"
              :aria-checked="locale === code"
              class="rounded px-4 py-2 text-sm transition disabled:opacity-50"
              :class="locale === code ? 'bg-emerald-600 font-medium text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'"
              :disabled="savingLocale"
              @click="changeLocale(code)"
            >
              {{ t(`settings.locale.${code}`) }}
            </button>
          </div>
        </section>

        <section class="rounded-lg border border-slate-700 bg-slate-900/40 p-5">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h3
                id="iis-limit-label"
                class="text-sm font-medium text-slate-300"
              >
                {{ t('settings.iisLimit') }}
              </h3>
              <p class="mt-1 text-xs text-slate-500">
                {{ t('settings.iisLimitHint') }}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              :aria-checked="iisLimitHint"
              aria-labelledby="iis-limit-label"
              class="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-50"
              :class="iisLimitHint ? 'bg-emerald-600' : 'bg-slate-700'"
              :disabled="savingPreferences"
              @click="toggleIisLimitHint"
            >
              <span
                class="inline-block size-6 rounded-full bg-white shadow transition-transform"
                :class="iisLimitHint ? 'translate-x-[22px]' : 'translate-x-0.5'"
              />
            </button>
          </div>
        </section>

        <SecuritySection
          @message="(text) => { error = ''; message = text; }"
          @error="(text) => { message = ''; error = text; }"
        />

        <NotificationsSection
          class="lg:col-span-2"
          @message="(text) => { error = ''; message = text; }"
          @error="(text) => { message = ''; error = text; }"
        />

        <section class="rounded-lg border border-slate-700 bg-slate-900/40 p-5 lg:col-span-2">
          <h3 class="text-sm font-medium text-slate-300">
            {{ t('settings.apiKeys') }}
          </h3>
          <p class="mt-1 text-xs text-slate-500">
            {{ t('settings.apiKeysHint') }}
          </p>
          <ul class="mt-4 space-y-4">
            <li
              v-for="row in apiRows"
              :key="row.source"
              class="rounded border border-slate-800 p-3"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p class="font-medium text-slate-200">
                    {{ sourceLabel(row) }}
                  </p>
                  <p class="text-xs text-slate-500">
                    {{ row.source }}
                    <span
                      v-if="row.configured"
                      class="ml-2 text-emerald-500"
                    >· {{ t('settings.keySaved') }}</span>
                  </p>
                </div>
              </div>
              <input
                v-model="keyDrafts[row.source]"
                type="password"
                autocomplete="off"
                :placeholder="row.configured ? t('settings.keyReplacePlaceholder') : t('settings.keyPlaceholder')"
                class="mt-3 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              >
              <div class="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded bg-emerald-600 px-3 py-1.5 text-sm hover:bg-emerald-500 disabled:opacity-50"
                  :disabled="savingKeySource === row.source"
                  @click="saveApiKey(row.source)"
                >
                  {{ savingKeySource === row.source ? t('common.saving') : t('settings.saveKey') }}
                </button>
                <button
                  v-if="row.configured"
                  type="button"
                  class="rounded bg-rose-900/60 px-3 py-1.5 text-sm text-rose-200 hover:bg-rose-800/60 disabled:opacity-50"
                  :disabled="savingKeySource === row.source"
                  @click="removeApiKey(row.source)"
                >
                  {{ t('common.remove') }}
                </button>
              </div>
            </li>
          </ul>
        </section>
      </div>
    </template>

    <p
      v-if="message"
      class="text-sm text-emerald-400"
    >
      {{ message }}
    </p>
    <p
      v-if="error"
      class="text-sm text-rose-400"
    >
      {{ error }}
    </p>
  </div>
</template>
